import apiClient from '@/api/client';

export interface ActionItem {
  type: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  action_url: string;
  count: number;
  entity_type?: string;
  due_date?: string;
}

export interface HRKPIs {
  headcount: number;
  on_leave_today: number;
  pending_leave_requests: number;
  open_positions: number;
  in_probation: number;
}

export interface PayrollKPIs {
  last_payroll_total: number;
  last_payroll_month: string | null;
  ytd_payroll_cost: string;
  current_month_status: string;
  payroll_runs_this_year: number;
}

export interface BudgetVarianceItem {
  category: string;
  budget: number;
  actual: number;
  variance: number;
  variance_pct: number;
}

export interface ReceivablesAgingItem {
  bucket: string;
  amount: number;
}

export interface HeadcountTrendItem {
  month: string;
  headcount: number;
}

export interface PayrollTrendItem {
  month: string;
  value: number;
}

export interface ExpenseBreakdownItem {
  name: string;
  value: number;
}

export interface GroupMonthlyTrendItem {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

export interface SubsidiaryKPI {
  company_id: string;
  company_name: string;
  colour: string;
  revenue: number;
  expenses: number;
  profit: number;
  margin: number;
  payroll: number;
  headcount: number;
  revenue_share: number;
}

export interface TaxComplianceItem {
  tax_item: string;
  total: number;
  status: 'not_applicable' | 'pending' | 'overdue';
  due_date: string;
  description: string;
  employee_portion?: number;
  employer_portion?: number;
}

export interface WelcomeIndustryTip {
  title: string;
  body: string;
}

export interface WelcomeHoliday {
  name: string;
  date: string;
}

export interface WelcomeData {
  company_name: string;
  industry: string | null;
  industry_label: string;
  country_code: string;
  days_active: number;
  industry_tips: WelcomeIndustryTip[];
  holidays_this_month: WelcomeHoliday[];
  moveable_holiday_reminders: string[];
  welcome_message: string;
}

export interface UpcomingHoliday {
  id: string;
  name: string;
  observed_date: string;
  is_full_day: boolean;
}

export const dashboardApi = {
  welcome: () =>
    apiClient.get<WelcomeData | null>('/dashboard/welcome'),
  upcomingHolidays: () =>
    apiClient.get<UpcomingHoliday[]>('/dashboard/upcoming-holidays'),
  kpis: (year: number) =>
    apiClient.get('/accounting/dashboard/kpis', { params: { year } }),
  groupKPIs: (year: number) =>
    apiClient.get('/dashboard/group/kpis', { params: { year } }),
  companyKPIs: (year: number) =>
    apiClient.get('/accounting/dashboard/kpis', { params: { year } }),
  revenueBreakdown: (year: number) =>
    apiClient.get('/accounting/dashboard/revenue-breakdown', { params: { year } }),
  monthlyTrend: (year: number) =>
    apiClient.get('/accounting/dashboard/monthly-trend', { params: { year } }),
  actionCenter: () =>
    apiClient.get<ActionItem[]>('/dashboard/action-center'),
  hrKpis: (year: number) =>
    apiClient.get<HRKPIs>('/dashboard/hr-kpis', { params: { year } }),
  payrollKpis: (year: number) =>
    apiClient.get<PayrollKPIs>('/dashboard/payroll-kpis', { params: { year } }),

  // New enhanced chart endpoints
  expenseBreakdown: (year: number) =>
    apiClient.get<ExpenseBreakdownItem[]>('/dashboard/company/charts/expense-breakdown', { params: { year } }),
  headcountTrend: (year: number) =>
    apiClient.get<HeadcountTrendItem[]>('/dashboard/company/charts/headcount-trend', { params: { year } }),
  payrollTrend: (year: number) =>
    apiClient.get<PayrollTrendItem[]>('/dashboard/company/charts/payroll-trend', { params: { year } }),
  budgetVariance: (year: number) =>
    apiClient.get<BudgetVarianceItem[]>('/dashboard/company/charts/budget-variance', { params: { year } }),
  receivablesAging: () =>
    apiClient.get<ReceivablesAgingItem[]>('/dashboard/company/charts/receivables-aging'),

  // Group dashboard endpoints
  groupMonthlyTrend: (year: number) =>
    apiClient.get<GroupMonthlyTrendItem[]>('/dashboard/group/monthly-trend', { params: { year } }),
  groupSubsidiaryKpis: (year: number) =>
    apiClient.get<SubsidiaryKPI[]>('/dashboard/group/subsidiary-kpis', { params: { year } }),
  groupTaxCompliance: (year: number, month: number) =>
    apiClient.get<TaxComplianceItem[]>('/dashboard/group/tax-compliance', { params: { year, month } }),
  groupActivityFeed: (limit?: number) =>
    apiClient.get('/dashboard/group/activity-feed', { params: { limit: limit || 20 } }),
};
